import React from "react";
import { Helmet } from "react-helmet-async";
import { Route, Routes, useNavigate } from "react-router-dom";
import OnboardingDashboard from "../../../components/charitySetup/onboarding/onboardingDashboard";
import CharitySettingsPage from "../../../components/charitySetup/onboarding/onboardingDashboard/charitySettingsPage";
import CustomizeHubPage from "../../../components/charitySetup/onboarding/onboardingDashboard/customizeHub";
import SendingEmailsMaintainPage from "../../../components/charitySetup/onboarding/onboardingDashboard/sendingEmailsMaintain";
import WebsiteMaintainPage from "../../../components/charitySetup/onboarding/onboardingDashboard/websiteMaintain";
import DashboardGate from "../../../components/charitySetup/onboarding/onboardingDashboard/dashboardGate";
import OnboardingAccessGate from "../../../components/charitySetup/onboarding/onboardingDashboard/onboardingAccessGate";
import MaintainAccessGate from "../../../components/charitySetup/onboarding/onboardingDashboard/maintainAccessGate";
import OrganizationSetupWizard from "../../../components/charitySetup/onboardingSteps/organizationSetup";
import PaymentSetup from "../../../components/charitySetup/onboardingSteps/paymentSetup";
import DomainConfigurationWizard from "../../../components/charitySetup/onboardingSteps/domainConfiguration";
import MmsSetupWizard from "../../../components/charitySetup/onboardingSteps/mmsSetup";
import BrandSetupWizard from "../../../components/charitySetup/onboardingSteps/brandSetup";
import Layout from "../../../components/Layout/layout";
import ProtectedRoute from "../../../../Auth/components/ProtectedRoute";
import {
  CHARITY_CUSTOMIZE_HUB_PATH,
  ONBOARDING_DASHBOARD_PATH,
} from "../../../../utils/onboarding/stepNavigation";

const OnboardingWizardLayout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <Layout>{children}</Layout>;

const OnboardingDashboardContainer = () => {
  const navigate = useNavigate();

  const handleWizardExit = () => {
    navigate(ONBOARDING_DASHBOARD_PATH);
  };

  const handleCustomizeHubExit = () => {
    navigate(CHARITY_CUSTOMIZE_HUB_PATH);
  };

  return (
    <ProtectedRoute>
      <main style={{ backgroundColor: "#F8FAFC" }}>
        <Helmet title="Onboarding Dashboard" />
        <Routes>
          <Route
            path="/"
            element={
              <Layout>
                <OnboardingAccessGate>
                  <DashboardGate gate="onboardingRequired">
                    <OnboardingDashboard />
                  </DashboardGate>
                </OnboardingAccessGate>
              </Layout>
            }
          />

          <Route
            path="settings"
            element={
              <Layout>
                <CharitySettingsPage />
              </Layout>
            }
          />
          <Route
            path="customize"
            element={
              <Layout>
                <MaintainAccessGate>
                  <CustomizeHubPage />
                </MaintainAccessGate>
              </Layout>
            }
          />
          <Route
            path="customize/organization"
            element={
              <MaintainAccessGate>
                <OnboardingWizardLayout>
                  <OrganizationSetupWizard
                    embedded={false}
                    maintainMode
                    onExit={handleCustomizeHubExit}
                  />
                </OnboardingWizardLayout>
              </MaintainAccessGate>
            }
          />
          <Route
            path="customize/sending-emails"
            element={
              <MaintainAccessGate>
                <OnboardingWizardLayout>
                  <SendingEmailsMaintainPage onExit={handleCustomizeHubExit} />
                </OnboardingWizardLayout>
              </MaintainAccessGate>
            }
          />
          <Route
            path="customize/website"
            element={
              <MaintainAccessGate>
                <OnboardingWizardLayout>
                  <WebsiteMaintainPage onExit={handleCustomizeHubExit} />
                </OnboardingWizardLayout>
              </MaintainAccessGate>
            }
          />

          <Route
            path="organization"
            element={
              <OnboardingAccessGate>
                <OnboardingWizardLayout>
                  <OrganizationSetupWizard
                    embedded={false}
                    onExit={handleWizardExit}
                  />
                </OnboardingWizardLayout>
              </OnboardingAccessGate>
            }
          />
          <Route
            path="donations"
            element={
              <OnboardingAccessGate>
                <DashboardGate gate="organizationComplete">
                  <OnboardingWizardLayout>
                    <PaymentSetup embedded={false} onExit={handleWizardExit} />
                  </OnboardingWizardLayout>
                </DashboardGate>
              </OnboardingAccessGate>
            }
          />
          <Route
            path="email-setup"
            element={
              <OnboardingAccessGate>
                <DashboardGate gate="organizationComplete">
                  <OnboardingWizardLayout>
                    <DomainConfigurationWizard
                      embedded={false}
                      onExit={handleWizardExit}
                    />
                  </OnboardingWizardLayout>
                </DashboardGate>
              </OnboardingAccessGate>
            }
          />
          <Route
            path="branding"
            element={
              <OnboardingAccessGate>
                <DashboardGate gate="organizationComplete">
                  <OnboardingWizardLayout>
                    <BrandSetupWizard
                      embedded={false}
                      onExit={handleWizardExit}
                    />
                  </OnboardingWizardLayout>
                </DashboardGate>
              </OnboardingAccessGate>
            }
          />
          <Route
            path="mms-setup"
            element={
              <OnboardingAccessGate>
                <DashboardGate gate="mmsUnlocked">
                  <OnboardingWizardLayout>
                    <MmsSetupWizard embedded={false} onExit={handleWizardExit} />
                  </OnboardingWizardLayout>
                </DashboardGate>
              </OnboardingAccessGate>
            }
          />
        </Routes>
      </main>
    </ProtectedRoute>
  );
};

export default OnboardingDashboardContainer;
